import { AdminMembersReportContentUsagesTableRowFragment$key } from "@/admin/insights/member-reports/tabs/contents/__generated__/AdminMembersReportContentUsagesTableRowFragment.graphql"
import { ContentUsageUtils } from "@/content-usage/ContentUsageUtils"
import ContentUtils from "@/content/util/contentUtils"
import { useLabel } from "@/core/context/LabelsContext"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoTable, DiscoText } from "@disco-ui"
import DiscoTag from "@disco-ui/tag/DiscoTag"
import { TableCell } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import { graphql, useFragment } from "react-relay"

interface Props {
  contentUsageKey: AdminMembersReportContentUsagesTableRowFragment$key
}

function AdminMembersReportContentUsagesTableRow({ contentUsageKey }: Props) {
  const classes = useStyles()
  const organizationLabel = useLabel("organization")
  const contentUsage = useFragment<AdminMembersReportContentUsagesTableRowFragment$key>(
    graphql`
      fragment AdminMembersReportContentUsagesTableRowFragment on ContentUsage
      @argumentDefinitions(organizationMembershipId: { type: "ID!" }) {
        id
        entity
        content {
          name
          type
          contentLabelId
        }
        product {
          name
        }
        module {
          isCurriculumModule
          isCollectionModule
          name
        }
        reportingContentView(organizationMembershipId: $organizationMembershipId) {
          id
        }
      }
    `,
    contentUsageKey
  )

  const contentLabels = ContentUtils.useContentLabels()
  const contentLabel = ContentUtils.useContentLabel({
    entity: contentUsage.entity,
    content: {
      type: contentUsage.content.type,
      label:
        contentLabels.find((cl) => cl.id === contentUsage.content?.contentLabelId)
          ?.label || "",
    },
  })

  return (
    <DiscoTable.Row>
      {/* Name */}
      <TableCell>
        <div className={classes.iconAndNameContainer}>
          <span className={classes.iconContainer}>
            {ContentUsageUtils.getContentEmptyThumbnail(contentUsage.content)}
          </span>
          <DiscoText variant={"body-sm"} truncateText={1}>
            {contentUsage.content.name}
          </DiscoText>
        </div>
      </TableCell>

      {/* Content Type */}
      <TableCell>
        <DiscoTag name={contentLabel} />
      </TableCell>

      {/* Content Location */}
      <TableCell>
        <div>
          <DiscoText variant={"body-sm-500"} truncateText={2}>
            {contentUsage.product?.name || organizationLabel.singular}
          </DiscoText>
        </div>
        <div>
          <DiscoText variant={"body-xs"} color={"groovy.grey.500"} truncateText={2}>
            {contentUsage.module
              ? contentUsage.module.isCurriculumModule
                ? `Module: ${contentUsage.module.name}`
                : `Folder: ${contentUsage.module.name}`
              : "Dashboard"}
          </DiscoText>
        </div>
      </TableCell>

      {/* Viewed */}
      <TableCell>{contentUsage.reportingContentView ? "Yes" : "No"}</TableCell>
    </DiscoTable.Row>
  )
}

const useStyles = makeUseStyles((theme) => ({
  iconAndNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  iconContainer: {
    backgroundColor: theme.palette.groovy.neutral[100],
    borderRadius: theme.measure.borderRadius.medium,
    padding: theme.spacing(1),
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
  },
}))

export function AdminMembersReportContentUsagesTableRowSkeleton() {
  return (
    <DiscoTable.Row>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
      <TableCell>
        <Skeleton variant={"rect"} />
      </TableCell>
    </DiscoTable.Row>
  )
}

export default AdminMembersReportContentUsagesTableRow
